import { forwardRef } from 'react';
import type { ChangeEvent, ReactNode } from 'react';

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
  /**
   * Second line under the label, wired to the radio via `aria-describedby`
   * (story 3-3b). This is how a DISABLED option carries its REASON: the option
   * stays in the list, greyed and unselectable, with the reason both visible
   * and announced — rather than being hidden, which leaves the user unable to
   * tell "not available to me" from "does not exist".
   */
  description?: string;
  /** Rich trailing content inside the option's label (chips, badges, counts). */
  content?: ReactNode;
}

type RadioSize = 'sm' | 'md';

const INPUT_SIZE_CLASS: Record<RadioSize, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
};

export interface RadioGroupProps {
  id?: string;
  name: string;
  value: string | undefined;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  options: RadioOption[];
  disabled?: boolean;
  invalid?: boolean;
  size?: RadioSize;
  /** Lays options out in a wrapping row instead of a column. */
  horizontal?: boolean;
}

// A plain <div> of NATIVE radios sharing one `name` — deliberately NOT
// role="radiogroup" on this div. FormField's `control="group"` chrome mode
// (Form.tsx) already wraps whatever this renders in a <fieldset>+<legend>:
// the legend supplies the group's accessible name via the fieldset's
// implicit "group" role, so an explicit ARIA role here would be redundant.
// More importantly, native same-`name` radios inside a <fieldset> ARE the
// canonical accessible radio-group pattern — the browser gives arrow-key
// navigation between the radios for free. A `role="radiogroup"` on a
// hand-rolled <div> would need hand-built roving-tabindex + arrow-key
// handling to match that native behaviour; reinventing it would be strictly
// worse than just using real radios.
//
// `ref` attaches to the FIRST radio, not "the group" (there is no single DOM
// node representing a group) — this is what RHF's `shouldFocusError` needs
// to move focus into the group on a validation error.
export const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(function RadioGroup(
  { id, name, value, onChange, onBlur, options, disabled, invalid, size = 'md', horizontal = false },
  ref,
) {
  return (
    <div className={horizontal ? 'flex flex-wrap gap-4' : 'space-y-2'}>
      {options.map((option, index) => {
        const optionId = `${id ?? name}-${index}`;
        const descriptionId = option.description ? `${optionId}-description` : undefined;
        const isDisabled = disabled || option.disabled;
        // Simple (label-only) options keep the original centred alignment
        // byte-for-byte; only an option that carries a description/content —
        // i.e. a taller block — switches to top alignment.
        const rich = Boolean(option.description || option.content);
        return (
          <div
            key={option.value}
            data-testid={`${id ?? name}-option`}
            data-disabled={isDisabled ? 'true' : undefined}
            className={`flex gap-2 ${rich ? 'items-start' : 'items-center'} ${isDisabled ? 'opacity-60' : ''}`}
          >
            <input
              ref={index === 0 ? ref : undefined}
              type="radio"
              id={optionId}
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={onChange}
              onBlur={onBlur}
              disabled={isDisabled}
              aria-invalid={invalid || undefined}
              aria-describedby={descriptionId}
              className={`focus-ring shrink-0 ${rich ? 'mt-1' : ''} ${INPUT_SIZE_CLASS[size]} border-border accent-primary ${
                invalid ? 'ring-2 ring-destructive' : ''
              }`}
            />
            <div className="min-w-0">
              <label htmlFor={optionId} className="text-sm text-foreground">
                {option.label}
              </label>
              {option.content}
              {option.description && (
                <p id={descriptionId} className="text-xs text-muted-foreground">
                  {option.description}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
});
